import type { Vector2 } from "../simulation/player-movement";

export type SteeringInputOwner = "arena" | "ui";
export type PointerKind = "mouse" | "pen" | "touch";

export interface JoystickPointerSample {
  readonly captureId: number;
  readonly id: number;
  readonly kind: PointerKind;
  readonly position: Vector2;
}

export interface JoystickBounds {
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
}

export interface JoystickSafeAreaInsets {
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
}

export interface VirtualJoystickConfig {
  readonly deadZoneRadius: number;
  readonly knobRadius: number;
  readonly ringRadius: number;
  readonly safeAreaInset: number;
}

export interface VirtualJoystickActions {
  readonly capture: (captureId: number) => void;
  readonly releaseCapture: (captureId: number) => void;
  readonly releaseSteering: () => void;
  readonly steer: (direction: Vector2) => void;
}

export interface VirtualJoystickSnapshot {
  readonly active: boolean;
  readonly center: Vector2 | null;
  readonly knob: Vector2 | null;
  readonly pointerKind: PointerKind | null;
}

export type JoystickMoveResult = "dead-zone" | "ignored" | "steered";

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, value));

const normalizedInset = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, value) : 0;

export const insetJoystickBounds = (
  bounds: JoystickBounds,
  insets: JoystickSafeAreaInsets,
): JoystickBounds => ({
  bottom: bounds.bottom - normalizedInset(insets.bottom),
  left: bounds.left + normalizedInset(insets.left),
  right: bounds.right - normalizedInset(insets.right),
  top: bounds.top + normalizedInset(insets.top),
});

export class VirtualJoystickInput {
  private captureId = 0;
  private center: Vector2 | null = null;
  private knob: Vector2 | null = null;
  private origin: Vector2 | null = null;
  private pointerId: number | null = null;
  private pointerKind: PointerKind | null = null;

  public constructor(
    private readonly config: VirtualJoystickConfig,
    private readonly actions: VirtualJoystickActions,
  ) {}

  public begin(
    owner: SteeringInputOwner,
    pointer: JoystickPointerSample,
    bounds: JoystickBounds,
  ): boolean {
    if (owner !== "arena" || this.pointerId !== null) {
      return false;
    }
    const margin = this.config.ringRadius + this.config.safeAreaInset;
    const minimumX = bounds.left + margin;
    const maximumX = bounds.right - margin;
    const minimumY = bounds.top + margin;
    const maximumY = bounds.bottom - margin;
    if (minimumX > maximumX || minimumY > maximumY) {
      return false;
    }
    this.pointerId = pointer.id;
    this.captureId = pointer.captureId;
    this.pointerKind = pointer.kind;
    this.origin = { ...pointer.position };
    this.center = {
      x: clamp(pointer.position.x, minimumX, maximumX),
      y: clamp(pointer.position.y, minimumY, maximumY),
    };
    this.knob = { ...this.center };
    this.actions.capture(pointer.captureId);
    return true;
  }

  public move(pointer: JoystickPointerSample): JoystickMoveResult {
    if (
      pointer.id !== this.pointerId ||
      this.center === null ||
      this.origin === null
    ) {
      return "ignored";
    }
    const displacement = {
      x: pointer.position.x - this.origin.x,
      y: pointer.position.y - this.origin.y,
    };
    const distance = Math.hypot(displacement.x, displacement.y);
    const scale =
      distance > this.config.ringRadius ? this.config.ringRadius / distance : 1;
    const clampedDisplacement = {
      x: displacement.x * scale,
      y: displacement.y * scale,
    };
    this.knob = {
      x: this.center.x + clampedDisplacement.x,
      y: this.center.y + clampedDisplacement.y,
    };
    if (distance < this.config.deadZoneRadius) {
      return "dead-zone";
    }
    this.actions.steer(clampedDisplacement);
    return "steered";
  }

  public end(pointerId: number): boolean {
    if (pointerId !== this.pointerId) {
      return false;
    }
    this.clear();
    return true;
  }

  public cancelCapture(captureId: number): boolean {
    if (this.pointerId === null || captureId !== this.captureId) {
      return false;
    }
    this.clear();
    return true;
  }

  public cancel(): boolean {
    if (this.pointerId === null) {
      return false;
    }
    this.clear();
    return true;
  }

  public getSnapshot(): VirtualJoystickSnapshot {
    return {
      active: this.pointerId !== null,
      center: this.center === null ? null : { ...this.center },
      knob: this.knob === null ? null : { ...this.knob },
      pointerKind: this.pointerKind,
    };
  }

  private clear(): void {
    const captureId = this.captureId;
    this.pointerId = null;
    this.captureId = 0;
    this.pointerKind = null;
    this.center = null;
    this.knob = null;
    this.origin = null;
    this.actions.releaseSteering();
    this.actions.releaseCapture(captureId);
  }
}
